<template>
  <v-card flat class="mb-4">
    <v-row dense>
      <v-col cols="12" sm="3">
        <v-autocomplete
          v-model="filters.user_id"
          :items="users"
          item-text="full_name"
          item-value="id"
          label="User"
          clearable
          dense
          outlined
          :multiple="multiUser"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.full_name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ item.branch ? item.branch.branch_name : "" }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" sm="2">
        <v-select
          v-model="period"
          :items="periodOptions"
          label="Period"
          dense
          outlined
          @change="onPeriodChange"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="2">
        <v-menu
          v-if="period !== 'year'"
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateDisplay"
              :label="dateLabel"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-if="period === 'day' || period === 'week'"
            v-model="selectedDate"
            @input="onDatePicked"
          ></v-date-picker>
          <v-date-picker
            v-else-if="period === 'month'"
            v-model="selectedMonth"
            type="month"
            @input="onMonthPicked"
          ></v-date-picker>
        </v-menu>
        <v-select
          v-else
          v-model="selectedYear"
          :items="yearOptions"
          label="Select Year"
          dense
          outlined
          @change="onYearChange"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="2" class="d-flex align-center">
        <span class="caption grey--text">{{ dateRangeDisplay }}</span>
      </v-col>
      <v-col cols="12" sm="1">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          label="Status"
          clearable
          dense
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" sm="1">
        <v-select
          v-model="filters.category"
          :items="categoryOptions"
          label="Category"
          clearable
          dense
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" sm="1" class="d-flex align-center">
        <v-btn icon color="primary" @click="applyFilters">
          <v-icon>mdi-filter</v-icon>
        </v-btn>
        <v-btn icon @click="resetFilters">
          <v-icon>mdi-filter-off</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  props: {
    multiUser: { type: Boolean, default: false },
  },
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      dateMenu: false,
      period: "day",
      selectedDate: today,
      selectedMonth: today.substr(0, 7),
      selectedYear: today.substr(0, 4),
      filters: {
        user_id: null,
        date_from: today,
        date_to: today,
        status: null,
        category: null,
      },
      periodOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
        { text: "Year", value: "year" },
      ],
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Ongoing", value: "ongoing" },
        { text: "Completed", value: "completed" },
        { text: "Acknowledged", value: "acknowledged" },
        { text: "Missed", value: "missed" },
      ],
      categoryOptions: [],
    };
  },
  computed: {
    ...mapGetters({
      users: "auth/users",
    }),
    dateLabel() {
      const map = {
        day: "Select Date",
        week: "Select Week",
        month: "Select Month",
        year: "Select Year",
      };
      return map[this.period] || "Select Date";
    },
    dateDisplay() {
      if (this.period === "month") return this.selectedMonth;
      if (this.period === "year") return this.selectedYear;
      return this.selectedDate;
    },
    dateRangeDisplay() {
      if (!this.filters.date_from || !this.filters.date_to) return "";
      if (this.filters.date_from === this.filters.date_to)
        return this.filters.date_from;
      return this.filters.date_from + " ~ " + this.filters.date_to;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push({ text: String(y), value: String(y) });
      }
      return years;
    },
  },
  created() {
    if (!this.users || this.users.length === 0) {
      this.get_users();
    }
    this.calculateDateRange();
  },
  methods: {
    ...mapActions({
      get_users: "auth/get_users",
    }),
    onPeriodChange() {
      this.calculateDateRange();
    },
    onDatePicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    onYearChange() {
      this.calculateDateRange();
    },
    onMonthPicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    calculateDateRange() {
      if (this.period === "day") {
        this.filters.date_from = this.selectedDate;
        this.filters.date_to = this.selectedDate;
      } else if (this.period === "week") {
        const d = new Date(this.selectedDate + "T00:00:00");
        const day = d.getDay();
        const diffToMon = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMon);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        this.filters.date_from = monday.toISOString().substr(0, 10);
        this.filters.date_to = sunday.toISOString().substr(0, 10);
      } else if (this.period === "month") {
        const parts = this.selectedMonth.split("-");
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        this.filters.date_from = firstDay.toISOString().substr(0, 10);
        this.filters.date_to = lastDay.toISOString().substr(0, 10);
      } else if (this.period === "year") {
        const year = this.selectedYear || this.selectedDate.substr(0, 4);
        this.filters.date_from = year + "-01-01";
        this.filters.date_to = year + "-12-31";
      }
    },
    applyFilters() {
      const clean = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key] !== null && this.filters[key] !== "") {
          clean[key] = this.filters[key];
        }
      });
      this.$emit("filter-change", clean);
    },
    resetFilters() {
      const today = new Date().toISOString().substr(0, 10);
      this.period = "day";
      this.selectedDate = today;
      this.selectedMonth = today.substr(0, 7);
      this.selectedYear = today.substr(0, 4);
      this.filters = {
        user_id: null,
        date_from: today,
        date_to: today,
        status: null,
        category: null,
      };
      this.$emit("filter-change", {});
    },
  },
};
</script>
